import { DateUtils, roundToNDigits } from "@audacia-hq/shared/utils";
import dayjs, { Dayjs } from "dayjs";
import { useRouter } from "next/navigation";
import * as React from "react";
import { useEffect, useState } from "react";
import Countdown, { CountdownRendererFn } from "react-countdown";
import { useTranslation } from "react-i18next";

import { useAppContext } from "../../context/AppContext";
import { useMember } from "../../context/MemberContext";
import { Boost } from "../../models/global";
import Button from "../Button";
import CustomIcon from "../icons/CustomIcon";
import WelcomeOfferBanner from "../members/WelcomeOfferBanner";

const MarketingBanner: React.FC = () => {
  const { t } = useTranslation("common");
  const { boosts, promotions } = useAppContext();
  const { member } = useMember();
  const router = useRouter();

  const [completedBoosts, setCompletedBoosts] = useState<string[]>([]);
  const [boostBanners, setBoostBanners] = useState<{ [key: string]: Boost }>();
  const [promoEndDate, setPromoEndDate] = useState<Dayjs>();
  const [isPromoInit, setIsPromoInit] = useState(false);

  useEffect(() => {
    if (!boosts) return;
    const sortAlphabetically = (a: string, b: string) => a.localeCompare(b);
    const banners = boosts
      .filter(({ boostUid }) => !completedBoosts.includes(boostUid))
      .reduce((acc, curr) => ({
        ...acc,
        [[...curr.channels].sort(sortAlphabetically).join("_")]: curr,
      }), {});
    setBoostBanners(banners);
  }, [boosts, completedBoosts]);

  useEffect(() => {
    if (!promotions) {
      setIsPromoInit(true);
    } else {
      const endAt = promotions.reduce<Dayjs>((aggr, promo) => {
        const end = dayjs(promo.endDate);
        if (!aggr) return end;
        return end.isAfter(aggr) ? end : aggr;
      }, undefined);
      setPromoEndDate(endAt);
      setIsPromoInit(true);
    }
  }, [promotions]);

  const getWrittenMinutes = (value: number) => roundToNDigits(0.15 * (100 - value), 2);

  const renderCountDown: CountdownRendererFn = ({
    days, hours, minutes, seconds,
  }) => (
    <>
      {days > 0 && <span>{`${days}${t("common:dayLetter")} `}</span>}
      {hours > 0 && <span>{`${hours}${t("common:hourLetter")} `}</span>}
      {minutes > 0 && <span>{`${minutes}${t("common:minuteLetter")} `}</span>}
      <span>{`${seconds}${t("common:secondLetter")}`}</span>
    </>
  );

  if (boostBanners && Object.keys(boostBanners).length > 0) {
    return (
      <>
        {Object.entries(boostBanners).map(([channel, boost]) => (
          <div
            key={`${boost.boostUid}-${channel}`}
            className="lg:animate-gradient bg-gradient-to-r from-fuchsia-700 via-purple-700 to-purple-700"
            data-pw={`boostBanner-${channel}`}
          >
            <div className="wrapper">
              <div className="flex w-full xl:w-auto justify-center items-center gap-2 sm:gap-4 px-2 py-3 text-white text-xs sm:text-sm xl:text-lg">
                <CustomIcon icon="sparkle" />
                <div className="flex-wrap md:flex gap-x-2 gap-y-1">
                  <div className="font-medium">
                    {channel === "WRITTEN"
                      ? t("boosts.WRITTEN", { minutes: getWrittenMinutes(boost.value) })
                      : t(`boosts.${channel}`, { percent: boost.value })}
                  </div>
                  <div className="whitespace-nowrap">
                    <Countdown
                      date={DateUtils.from(boost.endDate).getTime()}
                      renderer={renderCountDown}
                      onComplete={() => setCompletedBoosts((prev) => [...prev, boost.boostUid])}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </>
    );
  }

  if (promoEndDate) {
    return (
      <div
        className="lg:animate-gradient bg-gradient-to-r from-fuchsia-700 via-purple-700 to-purple-700"
        data-pw="promotionBanner"
      >
        <div className="wrapper">
          <div className="flex w-full xl:w-auto justify-center items-center gap-2 sm:gap-4 px-2 py-3 text-white text-xs sm:text-sm xl:text-lg">
            <CustomIcon icon="sparkle" />
            <div className="flex-wrap md:flex gap-x-2 gap-y-1">
              <div className="whitespace-nowrap font-medium">{t("promotion.generic")}</div>
              <div className="whitespace-nowrap">
                <Countdown
                  date={DateUtils.from(promoEndDate.toISOString()).getTime()}
                  renderer={renderCountDown}
                  onComplete={() => setPromoEndDate(undefined)}
                />
              </div>
            </div>
            <div className="whitespace-nowrap flex-grow sm:grow-0 text-right">
              <Button render="white" size="marketing" onClick={() => router.push("/packs")}>{t("promotion.seePacks")}</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isPromoInit && !promoEndDate && !member) {
    return <WelcomeOfferBanner />;
  }

  return null;
};

export default MarketingBanner;
